// Draw & Guess (Pictionary / Skribbl) Client
import { sounds } from '../sound.js';

export class DrawGuessClient {
  constructor(socket, containerEl) {
    this.socket = socket;
    this.container = containerEl;
    this.isDrawer = false;
    this.currentColor = '#000000';
    this.currentBrushSize = 4;
    this.isDrawing = false;
    this.lastX = 0;
    this.lastY = 0;

    this.render();
    this.setupSocketEvents();
  }

  render() {
    this.container.innerHTML = `
      <div class="dg-wrapper">
        <div class="dg-header">
          <div class="dg-info">
            <span id="dg-timer" class="dg-badge">⏳ 60s</span>
            <span id="dg-word-hint" class="dg-hint">_ _ _ _ _</span>
          </div>
          <div id="dg-drawer-badge" class="dg-drawer-badge">Drawer: 🎨 Choosing word...</div>
        </div>

        <div class="dg-main">
          <!-- Canvas Board -->
          <div class="dg-canvas-box">
            <canvas id="dg-canvas" width="700" height="460"></canvas>
            
            <!-- Word Choice Modal -->
            <div id="dg-word-modal" class="dg-modal modal-hidden">
              <h3>CHOOSE A WORD TO DRAW</h3>
              <div id="dg-options-box" class="dg-options-row"></div>
            </div>
          </div>

          <!-- Drawing Toolbar -->
          <div id="dg-toolbar" class="dg-toolbar disabled">
            <div class="color-palette">
              ${['#000000', '#ef4444', '#f97316', '#eab308', '#22c55e', '#38bdf8', '#a855f7', '#ec4899', '#ffffff'].map(c => `
                <button class="color-btn ${c === '#000000' ? 'active' : ''}" style="background:${c};" data-color="${c}"></button>
              `).join('')}
            </div>

            <div class="size-palette">
              <button class="size-btn active" data-size="3">●</button>
              <button class="size-btn" data-size="7">●●</button>
              <button class="size-btn" data-size="14">●●●</button>
            </div>

            <button id="dg-btn-clear" class="btn-tool">🗑️ Clear</button>
          </div>
        </div>

        <!-- Guess Input Box -->
        <div class="dg-guess-bar">
          <input type="text" id="dg-guess-input" placeholder="Type your guess here..." maxlength="30" />
          <button id="dg-btn-guess" class="btn-action">GUESS</button>
        </div>
      </div>
    `;

    this.canvas = document.getElementById('dg-canvas');
    this.ctx = this.canvas.getContext('2d');
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';

    this.setupCanvasEvents();
    this.setupToolbarEvents();
  }

  setupCanvasEvents() {
    const getPos = (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const scaleX = this.canvas.width / (rect.width || 1);
      const scaleY = this.canvas.height / (rect.height || 1);
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      };
    };

    const startDraw = (e) => {
      if (!this.isDrawer) return;
      e.preventDefault();
      try {
        this.canvas.setPointerCapture(e.pointerId);
      } catch (_) {}
      this.isDrawing = true;
      const pos = getPos(e);
      this.lastX = pos.x;
      this.lastY = pos.y;
    };

    const draw = (e) => {
      if (!this.isDrawing || !this.isDrawer) return;
      e.preventDefault();
      const pos = getPos(e);

      this.drawLine(this.lastX, this.lastY, pos.x, pos.y, this.currentColor, this.currentBrushSize);

      this.socket.emit('dg_stroke', {
        x0: this.lastX,
        y0: this.lastY,
        x1: pos.x,
        y1: pos.y,
        color: this.currentColor,
        size: this.currentBrushSize
      });

      this.lastX = pos.x;
      this.lastY = pos.y;
    };

    const stopDraw = (e) => {
      if (this.isDrawing) {
        this.isDrawing = false;
        try {
          if (e && e.pointerId && this.canvas.hasPointerCapture(e.pointerId)) {
            this.canvas.releasePointerCapture(e.pointerId);
          }
        } catch (_) {}
      }
    };

    this.canvas.addEventListener('pointerdown', startDraw);
    this.canvas.addEventListener('pointermove', draw);
    this.canvas.addEventListener('pointerup', stopDraw);
    this.canvas.addEventListener('pointercancel', stopDraw);
    this.canvas.addEventListener('pointerleave', stopDraw);
  }

  setupToolbarEvents() {
    const toolbar = document.getElementById('dg-toolbar');
    toolbar.querySelectorAll('.color-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        toolbar.querySelectorAll('.color-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.currentColor = btn.dataset.color;
      });
    });

    toolbar.querySelectorAll('.size-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        toolbar.querySelectorAll('.size-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.currentBrushSize = parseInt(btn.dataset.size);
      });
    });

    document.getElementById('dg-btn-clear')?.addEventListener('click', () => {
      if (this.isDrawer) {
        this.clearCanvas();
        this.socket.emit('dg_clear');
      }
    });

    const guessInput = document.getElementById('dg-guess-input');
    const sendGuess = () => {
      const text = guessInput.value.trim();
      if (!text) return;
      this.socket.emit('sendChatMessage', { text });
      guessInput.value = '';
    };

    document.getElementById('dg-btn-guess')?.addEventListener('click', sendGuess);
    guessInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') sendGuess();
    });
  }

  drawLine(x0, y0, x1, y1, color, size) {
    this.ctx.beginPath();
    this.ctx.moveTo(x0, y0);
    this.ctx.lineTo(x1, y1);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = size;
    this.ctx.stroke();
  }

  clearCanvas() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  setupSocketEvents() {
    this.socket.on('dg_selectWord', (data) => {
      const modal = document.getElementById('dg-word-modal');
      const box = document.getElementById('dg-options-box');
      if (modal && box) {
        box.innerHTML = '';
        data.options.forEach((word) => {
          const btn = document.createElement('button');
          btn.className = 'btn-word-opt';
          btn.innerText = word;
          btn.addEventListener('click', () => {
            this.socket.emit('dg_chooseWord', { word });
            modal.classList.add('modal-hidden');
          });
          box.appendChild(btn);
        });
        modal.classList.remove('modal-hidden');
      }
    });

    this.socket.on('dg_roundStarted', (data) => {
      this.clearCanvas();
      this.isDrawer = data.drawerId === this.socket.id;
      const toolbar = document.getElementById('dg-toolbar');
      const hint = document.getElementById('dg-word-hint');
      const badge = document.getElementById('dg-drawer-badge');

      if (this.isDrawer) {
        toolbar.classList.remove('disabled');
        badge.innerText = `You are Drawing!`;
      } else {
        toolbar.classList.add('disabled');
        badge.innerText = `Drawing: ${data.drawerName}`;
        hint.innerText = data.maskedWord;
      }
    });

    this.socket.on('dg_yourWord', (data) => {
      const hint = document.getElementById('dg-word-hint');
      if (hint) hint.innerText = `WORD: ${data.word}`;
    });

    this.socket.on('dg_drawStroke', (s) => {
      this.drawLine(s.x0, s.y0, s.x1, s.y1, s.color, s.size);
    });

    this.socket.on('dg_clearCanvas', () => {
      this.clearCanvas();
    });

    this.socket.on('dg_tick', (data) => {
      const timer = document.getElementById('dg-timer');
      if (timer) timer.innerText = `⏳ ${data.timer}s`;
    });

    this.socket.on('dg_correctGuess', (data) => {
      sounds.playSuccess();
    });

    this.socket.on('dg_turnEnded', (data) => {
      sounds.playChime();
      const hint = document.getElementById('dg-word-hint');
      if (hint) hint.innerText = `ANSWER: ${data.word}`;
    });

    this.socket.on('dg_gameOver', (data) => {
      sounds.playVictory();
    });
  }

  destroy() {
    this.socket.off('dg_selectWord');
    this.socket.off('dg_roundStarted');
    this.socket.off('dg_yourWord');
    this.socket.off('dg_drawStroke');
    this.socket.off('dg_clearCanvas');
    this.socket.off('dg_tick');
    this.socket.off('dg_correctGuess');
    this.socket.off('dg_turnEnded');
    this.socket.off('dg_gameOver');
  }
}
